var kind                        = require("enyo/kind"),
    throttle                    = require("enyo-perf/utils").throttle,
    PerformanceMode             = require("enyo-perf/PerformanceMode"),
    AbsoluteShowingQueueSupport = require("enyo-perf/AbsoluteShowingQueueSupport"),
    PerformanceModeQueueSupport = require("enyo-perf/PerformanceModeQueueSupport"),
    Canvas                      = require("enyo-canvas/Canvas"),
    EventDispatchSupport        = require("enyo-canvas/EventDispatchSupport"),
    Page                        = require("ott/image-reader/Page");

/**
 * [PageList]{@link ott.image-reader.PageList} displays a list of pages rendered
 * on a canvas tag that can be navigated by mouse/touch gestures or through
 * commands.
 *
 * It needs an adapter for the particular type of data it will receive - such as
 * [PageListArraySupport]{@link ott.image-reader.PageListArraySupport}, for
 * array data.
 *
 * @class   ott.image-reader.PageList
 * @extends enyo-canvas.Canvas
 * @mixes   enyo-perf.AbsoluteShowingQueueSupport
 * @mixes   enyo-perf.PerformanceModeQueueSupport
 * @mixes   enyo-canvas.EventDispatchSupport
 * @abstract
 * @public
 */
module.exports = kind(/** @lends ott.image-reader.PageList.prototype */ {

    /**
     * @private
     */
    name: "ott.image-reader.PageList",

    /**
     * @private
     */
    kind: Canvas,

    /**
     * @private
     */
    style: "touch-action: none;",

    /**
     * @private
     */
    mixins: [
        AbsoluteShowingQueueSupport,
        PerformanceModeQueueSupport,
        EventDispatchSupport
    ],

    /**
     * @private
     */
    classes: "ott-image-reader-page-list",

    /**
     * The kind that will be rendered for each record in the dataset.
     *
     * @type {enyo-canvas.CanvasControl}
     * @public
     */
    pageKind: Page,

    /**
     * Optional extra options to merge into the page kind at creation time.
     *
     * @type {Object=}
     * @public
     */
    pageOptions: null,

    /**
     * The amount of pages that will initially be preloaded.
     *
     * @type    {number}
     * @default 10
     * @public
     */
    bufferSize: 10,

    /**
     * Whether the buffering process should be asynchronous, adding one
     * page at at time.
     *
     * @type    {boolean}
     * @default false
     * @public
     */
    bufferAsync: false,

    /**
     * The debounce duration (in ms) between consecutive transitions. If
     * multiple transitions get requested within this time frame, only the
     * first and last one will get executed.
     *
     * @type    {number}
     * @default 250
     * @public
     */
    transitionTimeout: 250,

    /**
     * The dataset feeding this page list.
     *
     * @type {*}
     * @public
     */
    data: null,

    /**
     * The length of the dataset.
     *
     * @type {number}
     * @readonly
     * @public
     */
    length: 0,

    /**
     * This object maps the unique ids of records in the dataset to page
     * controls. This mapping will be updated any time a page is created or
     * destroyed.
     *
     * @type {Object}
     * @private
     */
    recordUidToPage: null,

    /**
     * An array of indexes for which we need to add pages.
     *
     * @type {Array}
     * @private
     */
    unbufferedIndexes: null,

    /**
     * A flag that tells us if the PageList is currently buffering pages.
     *
     * @type    {boolean}
     * @default false
     * @public
     */
    buffering: false,

    /**
     * The timeout that will trigger addition of the next page.
     *
     * @type {timeoutID}
     * @private
     */
    bufferTimeout: null,

    /**
     * The currently active page.
     *
     * @type {?number}
     * @readonly
     * @public
     */
    activePage: null,

    /**
     * Unthrottled reference to index from the last requested transition. It is
     * almost always best to use the snap and transition functions to navigate
     * through the list.
     *
     * Having said that, this property is exposed as public to provide a place
     * to designate for a specific page to be shown when the list is refreshed.
     *
     * @type {?number}
     * @public
     */
    requestedIndex: null,

    /**
     * Sets the direction in which the list is currently navigating. Supported
     * values are "forward" and "backward".
     *
     * @type    {?string}
     * @default "forward"
     * @public
     */
    direction: "forward",

    /**
     * @private
     */
    handlers: {
        onRequestNextPage:     "onRequestNextPage",
        onRequestPreviousPage: "onRequestPreviousPage"
    },

    // Initialization
    // --------------------------------

    /**
     * @method
     * @private
     */
    constructor: kind.inherit(function(sup) {
        return function() {
            this.recordUidToPage    = {};
            this.unbufferedIndexes  = [];
            this.bufferNext         = this.bindSafely(this.bufferNext);
            this._transitionToIndex = throttle(this.bindSafely(this._transitionToIndex), this.transitionTimeout);
            sup.apply(this, arguments);
        };
    }),

    /**
     * Initial viewport places world-coordinates 0,0 at the middle of the
     * canvas. Pixel coordinates are rounded down to avoid sub-pixel rendering,
     * which is better for drawing performance.
     *
     * @protected
     */
    setupViewport: function() {
        var bounds   = this.boundingClientRect,
            viewport = {
                width:  bounds.width,
                height: bounds.height
            };

        // Avoid non-integer pixels for speed
        viewport.x = Math.round(viewport.width / -2);
        viewport.y = Math.round(viewport.height / -2);
        this.renderer.setTransform(1, 0, 0, 1, -viewport.x, -viewport.y);
        this.set("viewport", viewport);
    },

    /**
     * Resize the canvas children in response too resized viewport.
     *
     * @param  {enyo-canvas.Viewport}  previousBounds  The previous bounds of
     *                                                 the canvas.
     * @param  {enyo-canvas.Viewport}  currentBounds   The current bounds of the
     *                                                 canvas.
     * @private
     */
    viewportChanged: function(previousBounds, currentBounds) {
        var xMultiplier,
            yMultiplier,
            multiplier,
            i,
            n,
            children = this.children;

        if (!previousBounds) {
            return;
        }

        // Calculate multiplier
        if (currentBounds.width !== 0 && previousBounds.width !== 0) {
            xMultiplier = currentBounds.width / previousBounds.width;
        }
        else {
            xMultiplier = 1;
        }

        if (currentBounds.height !== 0 && previousBounds.height !== 0) {
            yMultiplier = currentBounds.height / previousBounds.height;
        }
        else {
            yMultiplier = 1;
        }

        multiplier = xMultiplier < yMultiplier ? xMultiplier : yMultiplier;

        // Resize the children
        for (i = 0, n = children.length; i < n; i++) {
            children[i].reflow(multiplier);
        }
    },

    // Destruction
    // --------------------------------

    /**
     * @method
     * @private
     */
    destroy: kind.inherit(function(sup) {
        return function() {
            this._transitionToIndex.cancel();
            sup.apply(this, arguments);
        };
    }),

    // Dataset
    // --------------------------------

    /**
     * Gets a record from the dataset by index.
     *
     * @param  {number}  index  The index
     * @return {*}       The record, or `null` if not found.
     * @abstract
     * @protected
     */
    getRecordByIndex: function(index) {
        return this.error("PageList must implement a `getRecordByIndex` method.", index);
    },

    /**
     * Gets a record from the dataset by index.
     *
     * @param  {*}        record  The record
     * @return {?number}  The index, or `null` if not found.
     * @protected
     */
    getIndexByRecord: function(record) {
        return this.error("PageList must implement a `getIndexByRecord` method.", record);
    },

    /**
     * Retrieves a unique identifier from the record.
     *
     * @param  {*}       record  The record
     * @return {string}  The record uid.
     * @abstract
     * @protected
     */
    getRecordUid: function(record) {
        return this.error("PageList must implement a `getRecordUid` method.", record);
    },

    // Pages
    // --------------------------------

    /**
     * Creates a page control for the provided record.
     *
     * @param  {*}                          record  The record
     * @return {enyo-canvas.CanvasControl}  The created page
     * @private
     */
    addPage: function(record) {
        var recordUid       = this.getRecordUid(record),
            recordUidToPage = this.recordUidToPage,
            page;

        // Page already exists
        if (recordUidToPage[recordUid]) {
            this.error("Page for record: " + recordUid + " already exists.");

            return recordUidToPage[recordUid];
        }

        // Create page configured not to render immediately
        page = this.createComponent({
            name:     "pageList_page_" + recordUid,
            kind:     this.pageKind,
            classes:  "ott-image-reader-page-list__page",
            data:     record,
            pageList: this,
            showing:  false
        }, this.pageOptions || {});

        // Hide page by default, it'll be revealed in due time
        this.hidePage(page, "left", true);

        // Index page
        recordUidToPage[recordUid] = page;

        return page;
    },

    /**
     * Gets the page associated with the record at the provided index in the
     * dataset.
     *
     * @param  {number}                      index  The index
     * @return {?enyo-canvas.CanvasControl}  The page if found, `null` otherwise
     * @public
     */
    getPageForIndex: function(index) {
        var record,
            recordUid;

        if (!this.length || index < 0 || index >= this.length) {
            return null;
        }

        record    = this.getRecordByIndex(index);
        recordUid = this.getRecordUid(record);

        return this.recordUidToPage[recordUid] || null;
    },

    /**
     * Gets the page associated with the record at the provided index in the
     * dataset, a page will be created on the fly if needed.
     *
     * If the index is out-of-bounds, no page will be created.
     *
     * @param  {number}                      index  The index
     * @return {?enyo-canvas.CanvasControl}  The page if found, `null` otherwise
     * @private
     */
    ensurePageForIndex: function(index) {
        var page,
            record;

        if (!this.length || index < 0 || index >= this.length) {
            return null;
        }

        page = this.getPageForIndex(index);

        if (page) {
            return page;
        }

        record = this.getRecordByIndex(index);

        if (record) {
            return this.addPage(record);
        }

        return null;
    },

    /**
     * Makes sure the page is removed, no longer mapped to it's record and
     * destroyed.
     *
     * If the destroyed page was active or in the process of being activated, a
     * fallback index will be activated automatically.
     *
     * @param {Object}  record  The record
     * @private
     */
    removePage: function(record) {
        var recordUid = this.getRecordUid(record),
            page      = this.recordUidToPage[recordUid];

        if (page) {
            delete this.recordUidToPage[recordUid];
            page.destroy();

            if (page === this.activePage) {
                this.set("activePage", null);
                this.transitionToFallback(page);
            }
        }
    },

    /**
     * Remove pages that do not fall within the range of buffer indexes.
     *
     * @private
     */
    prunePages: function() {
        var buffer          = this.getBufferIndexes(),
            index,
            record,
            uid,
            whitelist       = {},
            recordUidToPage = this.recordUidToPage,
            recordUid,
            page;

        // Build a whitelist of pages that should not be removed
        while (buffer.length) {
            index  = buffer.pop();
            record = this.getRecordByIndex(index);

            if (record) {
                uid = this.getRecordUid(record);
            }

            if (uid !== null && uid !== undefined) {
                whitelist[uid] = true;
            }
        }

        // Remove all pages that are not on the whitelist, not active and not
        // visible
        for (recordUid in recordUidToPage) {
            if (recordUidToPage.hasOwnProperty(recordUid)) {
                page = recordUidToPage[recordUid];

                if (!whitelist[recordUid]
                    && page !== this.activePage
                    && !page.transform.isVisible()
                ) {
                    this.removePage(page.data);
                }
            }
        }
    },

    /**
     * Removes all pages
     *
     * @public
     */
    clearPages: function() {
        var recordUidToPage = this.recordUidToPage,
            recordUid;

        for (recordUid in recordUidToPage) {
            if (recordUidToPage.hasOwnProperty(recordUid)) {
                recordUidToPage[recordUid].destroy();
                delete recordUidToPage[recordUid];
            }
        }

        this.set("activePage", null);
        this.requestedIndex = null;
    },

    // Reflow
    // --------------------------------

    /**
     * Prune pages to reduce scope of the reflow.
     *
     * @method
     * @private
     */
    reflow: kind.inherit(function(sup) {
        return function() {
            this.prunePages();
            sup.apply(this, arguments);
        };
    }),

    // Page Buffering
    // --------------------------------

    /**
     * Creates a list of indexes for which pages should be preloaded and adds
     * them as needed.
     *
     * @public
     */
    updateBuffer: function() {
        var unbufferedIndexes;

        // Defer if not showing
        if (!this.absoluteShowing) {
            this.addToAbsoluteShowingQueue("updateBuffer");

            return;
        }
        this.removeFromAbsoluteShowingQueue("updateBuffer");

        // Defer when in performanceMode
        if (PerformanceMode.on) {
            this.addToPerformanceModeQueue("updateBuffer");

            return;
        }
        this.removeFromPerformanceModeQueue("updateBuffer");

        if (!this.buffering) {
            unbufferedIndexes = this.getBufferIndexes();

            if (unbufferedIndexes.length) {
                this.set("buffering", true);
                this.unbufferedIndexes = unbufferedIndexes;
                this.bufferNext();
            }
        }
    },

    /**
     * Calculates a sorted list of indexes for which to add a page. The length
     * of the list will be limited to the
     * [bufferSize]{@link ott.image-reader.PageList#bufferSize}.
     *
     * @return {Array}  The indexes.
     * @private
     */
    getBufferIndexes: function() {
        var length      = this.length,
            bufferSize  = this.bufferSize,
            minIndex    = 0,
            maxIndex    = length - 1,
            direction   = this.direction,
            activeIndex = this.getActiveIndex(),
            lowestIndex,
            highestIndex,
            indexes     = [];

        if (length < 1 || bufferSize < 1 || activeIndex === null) {
            return indexes;
        }

        lowestIndex  = activeIndex;
        highestIndex = lowestIndex;

        indexes.push(lowestIndex);

        while (indexes.length < bufferSize && (
            lowestIndex > minIndex || highestIndex < maxIndex
        )) {
            if (direction === "forward") {
                if (highestIndex < maxIndex) {
                    ++highestIndex;
                    indexes.push(highestIndex);
                }

                if (lowestIndex > minIndex) {
                    direction = "backward";
                }
            }
            else {
                if (lowestIndex > minIndex) {
                    --lowestIndex;
                    indexes.push(lowestIndex);
                }

                if (highestIndex < maxIndex) {
                    direction = "forward";
                }
            }
        }

        return indexes.reverse();
    },

    /**
     * Evaluates if there are any unbuffered indexes. If so, an index is popped
     * off the stack and a page added as needed.
     *
     * This method will be deferred if the page list is not absolutely showing
     * or if the application is in performance mode.
     *
     * @protected
     */
    bufferNext: function() {
        var bufferAsync       = this.bufferAsync,
            unbufferedIndexes = this.unbufferedIndexes;

        // Defer if not showing
        if (!this.absoluteShowing) {
            this.addToAbsoluteShowingQueue("bufferNext");

            return;
        }
        this.removeFromAbsoluteShowingQueue("bufferNext");

        // Defer when in performanceMode
        if (PerformanceMode.on) {
            this.addToPerformanceModeQueue("bufferNext");

            return;
        }
        this.removeFromPerformanceModeQueue("bufferNext");

        // Clear timeout reference so a new timeout can be scheduled
        if (bufferAsync) {
            this.bufferTimeout = null;
        }

        // Schedule next page if needed, stop otherwise.
        if (unbufferedIndexes.length === 0) {
            this.stopBuffering();

            return;
        }

        // Ensure a page for the next index
        this.ensurePageForIndex(unbufferedIndexes.pop());

        // Schedule next index
        bufferAsync ? this.asyncBufferNext() : this.bufferNext();
    },

    /**
     * Schedules bufferNext call to be triggered at the end of the stack.
     *
     * @private
     */
    asyncBufferNext: function() {
        if (!this.bufferTimeout) {
            this.bufferTimeout = setTimeout(this.bufferNext, 0);
        }
    },

    /**
     * Stops any currently active buffering loop.
     *
     * @public
     */
    stopBuffering: function() {
        this.removeFromAbsoluteShowingQueue("bufferNext");
        this.removeFromPerformanceModeQueue("bufferNext");

        if (this.bufferTimeout) {
            clearTimeout(this.bufferTimeout);
            this.bufferTimeout = null;
        }
        this.set("buffering", false);
    },

    /**
     * Continues any previously interrupted buffering loop.
     *
     * @public
     */
    continueBuffering: function() {
        if (this.unbufferedIndexes.length > 0) {
            this.set("buffering", true);
            this.bufferAsync ? this.asyncBufferNext() : this.bufferNext();
        }
    },

    // Page activity
    // --------------------------------

    /**
     * Mark a particular page as active.
     *
     * @param  {enyo-canvas.CanvasControl}  page  The page
     * @param  {boolean}                    snap  Whether to snap the transition
     * @public
     */
    activatePage: function(page, snap) {
        var activePage = this.activePage,
            index,
            activeIndex,
            direction;

        // Page is already active
        if (activePage === page) {
            this.requestedIndex = null;

            return;
        }

        // Mark the page's index as the last index to be requested
        this.requestedIndex = this.getIndexByRecord(page.data);

        // Get indexes
        index       = this.requestedIndex;
        activeIndex = this.getActiveIndex();

        // If this is our first active page, set paging direction to forward.
        if (activeIndex === null) {
            this.set("direction", "forward");
        }

        // If index changed we need to re-determine the paging direction
        else if (index !== activeIndex) {
            this.set("direction", index > activeIndex ? "forward" : "backward");
        }

        // Hide active page
        if (activePage) {
            direction = activeIndex < index ? "left" : "right";
            this.hidePage(activePage, direction, snap);
        }

        // If new page isn't interacting with any pointers, animate it into view
        if (page.pointers.length === 0) {
            direction = activeIndex === null || index > activeIndex ? "left" : "right";
            this.revealPage(page, direction, snap);
        }

        // Set active page
        this.set("activePage", page);
    },

    /**
     * Makes sure the previously active page is no longer marked as active.
     * Clear requestedIndex if it matches the activated page's index.
     *
     * @param  {enyo-canvas.CanvasControl}  previousPage  The previously active
     *                                                    page
     * @private
     */
    activePageChanged: function(previousPage) {
        var activePage = this.activePage;

        if (previousPage !== activePage) {
            previousPage && previousPage.set("active", false);
            activePage && activePage.set("active", true);
        }

        // Ensure newly activated page is no longer scheduled for activation
        if (this.requestedIndex !== null
            && activePage === this.getPageForIndex(this.requestedIndex)) {
            this.requestedIndex = null;
        }

        // Update buffered set of pages.
        activePage !== null && this.updateBuffer();
    },

    /**
     * Returns the index of of the current active page. If no page is active,
     * a value of 'null' is returned.
     *
     * @return {?number} The index of the active page, or `null` if none exists
     * @public
     */
    getActiveIndex: function() {
        var activePage = this.activePage;

        return activePage !== null ? this.getIndexByRecord(activePage.data) : null;
    },

    /**
     * Hide a page by sliding it out of view.
     *
     * @param  {enyo-canvas.CanvasControl}  page       The page to hide
     * @param  {string}                     direction  The direction in which to
     *                                                 slide
     * @param  {boolean}                    snap       Whether to snap the
     *                                                 transition
     * @public
     */
    hidePage: function(page, direction, snap) {
        var transform = page.transform;

        // Animate page out of view as needed
        transform.set("targetScaleX", "contain");
        transform.set("targetScaleY", "contain");
        transform.set("targetPositionX", direction === "left" ? "hideLeft" : "hideRight");
        transform.set("targetPositionY", "center");
        snap && transform.finish();
    },

    /**
     * Reveal a page by sliding it into view.
     *
     * @param  {enyo-canvas.CanvasControl}  page       The page to reveal
     * @param  {string}                     direction  The direction in which to
     *                                                 slide
     * @param  {boolean}                    snap       Whether to snap the
     *                                                 transition
     * @public
     */
    revealPage: function(page, direction, snap) {
        var transform = page.transform;

        // If the page is not yet visible, place it just outside of view.
        if (!transform.isVisible()) {
            this.hidePage(page, direction === "left" ? "right" : "left", true);
        }

        // Define centered target position for page.
        transform.set("targetScaleX", "contain");
        transform.set("targetScaleY", "contain");
        transform.set("targetPositionX", "center");
        transform.set("targetPositionY", "center");
        snap && transform.finish();
    },

    // Events from the pages themselves
    // --------------------------------

    /**
     * @param    {Control}  sender  The sender of the event
     * @param    {Object}   event   The event object
     * @returns  {boolean}  Stop propagating the event
     * @private
     */
    onRequestPreviousPage: function(sender, event) {
        if (event.snap) {
            this.snapToPrevious();
        }
        else {
            this.transitionToPrevious();
        }

        return true;
    },

    /**
     * @param    {Control}  sender  The sender of the event
     * @param    {Object}   event   The event object
     * @returns  {boolean}  Stop propagating the event
     * @private
     */
    onRequestNextPage: function(sender, event) {
        if (event.snap) {
            this.snapToNext();
        }
        else {
            this.transitionToNext();
        }

        return true;
    },

    // Snap
    // --------------------------------

    /**
     * Snaps to a particular page.
     *
     * @param  {enyo-canvas.CanvasControl}  page  The page to activate
     * @public
     */
    snapToPage: function(page) {
        this.activatePage(page, true);
    },

    /**
     * Snaps to a particular index.
     *
     * @param  {number}  index  The index to activate
     * @public
     */
    snapToIndex: function(index) {
        var page;

        page = this.ensurePageForIndex(index);

        if (page) {
            this.snapToPage(page);
        }
    },

    /**
     * Snaps to the first page.
     *
     * @public
     */
    snapToFirst: function() {
        this.snapToIndex(0);
    },

    /**
     * Snaps to the previous page.
     *
     * @public
     */
    snapToPrevious: function() {
        var index = this.requestedIndex;

        if (index === null) {
            index = this.getActiveIndex();
        }

        if (index !== null && index > 0) {
            --index;
        }

        if (index === null && this.length) {
            index = 0;
        }

        index !== null && this.snapToIndex(index);
    },

    /**
     * Snaps to the next page.
     *
     * @public
     */
    snapToNext: function() {
        var index = this.requestedIndex;

        if (index === null) {
            index = this.getActiveIndex();
        }

        if (index !== null && index < this.length - 1) {
            ++index;
        }

        if (index === null && this.length) {
            index = 0;
        }

        index !== null && this.snapToIndex(index);
    },

    /**
     * Snaps to the last page.
     *
     * @public
     */
    snapToLast: function() {
        this.snapToIndex(this.length - 1);
    },

    // Transition
    // --------------------------------

    /**
     * Transitions to a particular index.
     *
     * Transitions are throttled to prevent too many animating pages at once.
     * Once a transition is applied it'll apply the [last requested
     * index]{@link ott.image-reader.PageList#requestedIndex}.
     *
     * @param  {number}  index  The index to activate
     * @public
     */
    transitionToIndex: function(index) {
        var activeIndex;

        if (index >= 0 && index < this.length) {
            activeIndex = this.getActiveIndex();

            // If this is our first active page, set direction to forward.
            if (activeIndex === null) {
                this.set("direction", "forward");
            }

            // If index changed we need to re-determine the paging direction
            else if (index !== activeIndex) {
                this.set("direction", index > activeIndex ? "forward" : "backward");
            }

            this.requestedIndex = index;
            this._transitionToIndex();
        }
    },

    /**
     * Transitions to a particular page.
     *
     * Transitions are throttled to prevent too many animating pages at once.
     * Once a transition is applied it'll apply the [last requested
     * index]{@link ott.image-reader.PageList#requestedIndex}.
     *
     * @param  {enyo-canvas.CanvasControl}  page  The page to activate
     * @public
     */
    transitionToPage: function(page) {
        var index;

        index = this.getIndexByRecord(page.data);

        if (index || index === 0) {
            this.transitionToIndex(index);
        }
    },

    /**
     * Transitions to the first page.
     *
     * @public
     */
    transitionToFirst: function() {
        this.transitionToIndex(0);
    },

    /**
     * Transitions to the previous page.
     *
     * Transitions are throttled to prevent too many animating pages at once.
     * Once a transition is applied it'll apply the [last requested
     * index]{@link ott.image-reader.PageList#requestedIndex}.
     *
     * @public
     */
    transitionToPrevious: function() {
        var index = this.requestedIndex;

        if (index === null) {
            index = this.getActiveIndex();
        }

        if (index !== null && index > 0) {
            --index;
        }

        if (index === null && this.length) {
            index = 0;
        }

        index !== null && this.transitionToIndex(index);
    },

    /**
     * Transitions to the next page.
     *
     * Transitions are throttled to prevent too many animating pages at once.
     * Once a transition is applied it'll apply the [last requested
     * index]{@link ott.image-reader.PageList#requestedIndex}.
     *
     * @public
     */
    transitionToNext: function() {
        var index = this.requestedIndex;

        if (index === null) {
            index = this.getActiveIndex();
        }

        if (index !== null && index < this.length - 1) {
            ++index;
        }

        if (index === null && this.length) {
            index = 0;
        }

        index !== null && this.transitionToIndex(index);
    },

    /**
     * Transitions to the last page.
     *
     * @public
     */
    transitionToLast: function() {
        this.transitionToIndex(this.length - 1);
    },

    /**
     * Searches for pages at lower indexes until it finds one. If none is found
     * it'll look at higher indexes until it finds one.
     *
     * If a fallback page is encountered the page list will transition to it,
     * otherwise no further action is taken.
     *
     * Transitions are throttled to prevent too many animating pages at once.
     * Once a transition is applied it'll apply the [last requested
     * index]{@link ott.image-reader.PageList#requestedIndex}.
     *
     * @param  {enyo-canvas.CanvasControl}  page  The page for which to find a
     *                                            fallback
     * @private
     */
    transitionToFallback: function(page) {
        var index    = this.getIndexByRecord(page.data),
            minIndex = index,
            maxIndex = index,
            fallback;

        while (minIndex > 0 && !fallback) {
            --minIndex;
            fallback = this.getPageForIndex(minIndex);
        }

        while (maxIndex < this.length - 1 && !fallback) {
            ++maxIndex;
            fallback = this.getPageForIndex(maxIndex);
        }

        fallback && this.transitionToPage(fallback);
    },

    /**
     * Transitions to the page for
     * [requestedIndex]{@link ott.image-reader.PageList#requestedIndex}.
     *
     * @private
     */
    _transitionToIndex: function() {
        var index = this.requestedIndex,
            page;

        // Nothing to transition to
        if (index === null) {
            return;
        }

        page = this.ensurePageForIndex(index);

        // Activate page as needed
        if (page) {
            this.activatePage(page, false);
        }
        else {
            this.requestedIndex = null;
        }
    }

});
